import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import TabBar from '../components/TabBar';
import api from '../utils/api';
import { beijingToday } from '../utils/datetime';
import { isNativeApp, isStandaloneDisplay, isGuideDismissed, dismissInstallGuide } from '../utils/installState';
import IOSInstallGuide from '../components/IOSInstallGuide';
import EarningsPanel from '../components/EarningsPanel';
import { User, Download, Smartphone, CheckCircle, Wallet, ChevronRight, Video, ScanLine, Puzzle } from 'lucide-react';

// ── 平台检测 ───────────────────────────────────────────────────────────────
function usePlatform() {
  const ua = navigator.userAgent || '';
  const isNative    = isNativeApp();                                            // 已是 APK
  const isIOS       = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid   = /Android/.test(ua);
  const isStandalone = isStandaloneDisplay();                                   // 已是 PWA（Android / iOS / 桌面）
  return { isNative, isIOS, isAndroid, isStandalone };
}

// ── 主页面 ──────────────────────────────────────────────────────────────────
export default function MePage() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [wdAddress, setWdAddress]       = useState(null);
  const [wdHasActive, setWdHasActive]   = useState(false);
  const [addrModal, setAddrModal]       = useState(false);
  const [addrInput, setAddrInput]       = useState('');
  const [wdAvailable, setWdAvailable]   = useState(false);   // ★ 直属上级已参与 USDT 体系
  const [addrSaving, setAddrSaving]     = useState(false);
  const [vrPendingCount, setVrPendingCount] = useState(0);
  const [puzzleStats, setPuzzleStats] = useState(null);
  const [legacySum, setLegacySum] = useState(null);   // 排五今日识别小计

  useEffect(() => {
    api.get('/withdrawal/info').then(r => {
      setWdAddress(r.data.trc20_address ?? null);
      setWdHasActive(r.data.has_active ?? false);
      setWdAvailable(r.data.available ?? false);   // ★
    }).catch(() => {});
    api.get('/video-review/mine/pending-count').then(r => {
      setVrPendingCount(r.data.count || 0);
    }).catch(() => {});
    api.get('/puzzle/stats/mine').then(r => setPuzzleStats(r.data)).catch(() => {});
    api.get('/images/my-upload-groups').then(r => {
      const sum = { qualified: 0, duplicate: 0, invalid: 0, failed: 0 };
      const today = beijingToday();
      (r.data.groups || []).filter(g => g.uploadDate === today).forEach(g => { const o = g.ocr || {}; sum.qualified += o.qualified || 0; sum.duplicate += o.duplicate || 0; sum.invalid += o.invalid || 0; sum.failed += o.failed || 0; });
      setLegacySum(sum);
    }).catch(() => {});
  }, []);
  const { isNative, isIOS, isAndroid, isStandalone } = usePlatform();

  const APK_URL = '/app.apk';

  // 判断是否显示安装引导
  // 已经在 App / PWA 里运行就一律不再显示；iOS 在 Safari 标签页里无法得知是否装过，用户点“我已安装”后不再提示
  const [guideDismissed, setGuideDismissed] = useState(isGuideDismissed());
  const showIOSGuide     = isIOS && !isStandalone && !isNative && !guideDismissed;
  const showAndroidDownload = isAndroid && !isNative && !isStandalone;
  const showAnyInstall   = showIOSGuide || showAndroidDownload;

  return (
    <div className="page">
      <div className="header">
        <User size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
        我的
      </div>

      <div style={{ padding: '12px 16px' }}>

        {/* 用户信息卡 */}
        <div className="card" style={{ textAlign: 'center', padding: 24, marginBottom: 12 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--c-primary-light)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <User size={28} color="var(--c-primary)" />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{user?.name || user?.phone}</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginTop: 4 }}>
            {user?.phone}
          </div>
          {isNative && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--c-success)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Smartphone size={13} />已安装原生应用
            </div>
          )}
          {isStandalone && !isNative && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--c-success)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <CheckCircle size={13} />已添加到桌面
            </div>
          )}
        </div>

        {/* 我的收益（排五 + 证券 合计）+ 可提现余额；收款地址变化时 refreshKey 变，余额卡跟着刷新 */}
        <EarningsPanel refreshKey={wdAddress} />

        {/* 安装入口 */}
        {showAnyInstall && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 6 }}>
              <Smartphone size={16} color="var(--c-primary)" />
              安装到手机
            </div>

            {/* Android：下载 APK */}
            {showAndroidDownload && (
              <a href={APK_URL} download="React.apk"
                style={{ textDecoration: 'none' }}>
                <button className="btn btn-primary" style={{ width: '100%', height: 46,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Download size={18} />
                  下载安卓安装包（APK）
                </button>
              </a>
            )}

            {/* iOS：PWA 安装引导 */}
            {showIOSGuide && (
              <>
                <IOSInstallGuide />
                <button className="btn btn-outline" style={{ width: '100%', height: 40, marginTop: 12, fontSize: 13 }}
                  onClick={() => { dismissInstallGuide(); setGuideDismissed(true); }}>
                  我已安装，不再提示
                </button>
              </>
            )}
          </div>
        )}

        {/* 识别记录：排五 / 证券 两个入口（原底部“OCR”标签并入这里） */}
        <div className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px 4px', fontSize: 12, fontWeight: 600, color: 'var(--c-text-secondary)' }}>识别记录</div>
          <div onClick={() => navigate('/puzzle/records')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Puzzle size={16} color="var(--c-primary)" />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>证券识别记录</div>
                <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 1 }}>
                  今日 已计入 {puzzleStats?.counted_today ?? 0} · 待核实 {puzzleStats?.pending_today ?? 0} · 已作废 {puzzleStats?.rejected_today ?? 0}
                </div>
              </div>
            </div>
            <ChevronRight size={14} color="var(--c-text-tertiary)" />
          </div>
          <div style={{ height: 0.5, background: 'var(--c-divider)', margin: '0 16px' }} />
          <div onClick={() => navigate('/history')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ScanLine size={16} color="var(--c-primary)" />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>排五识别记录</div>
                <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 1 }}>
                  今日 成功 {legacySum?.qualified ?? 0} · 重复 {legacySum?.duplicate ?? 0} · 无效 {legacySum?.invalid ?? 0} · 失败 {legacySum?.failed ?? 0}
                </div>
              </div>
            </div>
            <ChevronRight size={14} color="var(--c-text-tertiary)" />
          </div>
        </div>

        {/* 二次审查任务入口 */}
        <div className="card"
          onClick={() => navigate('/video-review')}
          style={{ cursor: 'pointer', marginBottom: 12, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Video size={16} color="var(--c-primary)" />
              <span style={{ fontSize: 14, fontWeight: 600 }}>二次审查任务</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {vrPendingCount > 0 && (
                <span style={{
                  background: 'var(--c-danger)', color: '#fff', borderRadius: 10,
                  fontSize: 10, fontWeight: 700, padding: '1px 6px',
                  minWidth: 16, textAlign: 'center',
                }}>
                  {vrPendingCount}
                </span>
              )}
              <ChevronRight size={14} color="var(--c-text-tertiary)" />
            </div>
          </div>
        </div>

        {/* 收款方式 — 仅上级已参与 USDT 体系（wdAvailable）时显示 */}
        {wdAvailable && (
        <>
        <div className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', cursor: 'pointer' }}
            onClick={() => { setAddrInput(wdAddress || ''); setAddrModal(true); }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Wallet size={16} color="var(--c-primary)" />
              <span style={{ fontSize: 14, fontWeight: 600 }}>收款方式</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {!wdAddress ? (
                <span style={{ fontSize: 12, color: 'var(--c-text-tertiary)' }}>未开通</span>
              ) : wdHasActive ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--c-primary)', fontWeight: 600 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--c-primary)', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
                  监控中
                </span>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--c-success)', fontWeight: 600 }}>已开通</span>
              )}
              <ChevronRight size={14} color="var(--c-text-tertiary)" />
            </div>
          </div>
        </div>

        {/* 绑定地址弹窗 */}
        {addrModal && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'flex-end', zIndex: 999,
          }} onClick={() => setAddrModal(false)}>
            <div style={{
              width: '100%', background: 'var(--c-surface)', borderRadius: '20px 20px 0 0',
              padding: '24px 20px 48px',
            }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>收款地址设置</div>
              <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginBottom: 16 }}>TRC-20 网络 USDT 收款地址（T 开头 34 位）</div>
              <input className="input" placeholder="输入 TRC-20 地址"
                value={addrInput} onChange={e => setAddrInput(e.target.value.trim())}
                style={{ marginBottom: 16, letterSpacing: '0.04em', fontSize: 13 }}
              />
              <button
                className="btn btn-primary"
                style={{ width: '100%', height: 48, fontSize: 15, fontWeight: 600, borderRadius: 14, marginBottom: wdAddress ? 10 : 0 }}
                disabled={addrSaving || !/^T[A-Za-z0-9]{33}$/.test(addrInput)}
                onClick={async () => {
                  setAddrSaving(true);
                  try {
                    const r = await api.post('/withdrawal/address', { address: addrInput });
                    setWdAddress(addrInput);
                    setWdHasActive(false); // 刚绑定，无在途申请
                    setAddrModal(false);
                    // isFirst 由后端返回，首次绑定提示不同文案
                    showToast(r.data.isFirst ? '收款已开通，后续结算金额将自动累计至余额' : '收款地址已更新', 'success');
                  } catch (err) {
                    showToast(err.response?.data?.error || '保存失败，请重试', 'error');
                  } finally { setAddrSaving(false); }
                }}>
                {addrSaving ? '保存中…' : '保存'}
              </button>
              {/* 已开通时才显示关闭收款按钮 */}
              {wdAddress && (
                <button
                  className="btn btn-outline"
                  style={{ width: '100%', height: 44, fontSize: 14, borderRadius: 14, color: 'var(--c-danger)', borderColor: 'var(--c-danger)' }}
                  disabled={addrSaving}
                  onClick={async () => {
                    setAddrSaving(true);
                    try {
                      await api.delete('/withdrawal/address');
                      setWdAddress(null);
                      setWdHasActive(false);
                      setAddrModal(false);
                      showToast('收款已关闭，后续结算不再累计余额', 'success');
                    } catch (err) {
                      showToast(err.response?.data?.error || '关闭失败，请重试', 'error');
                    } finally { setAddrSaving(false); }
                  }}>
                  关闭收款
                </button>
              )}
            </div>
          </div>
        )}

        </>
        )}  {/* /wdAvailable */}

        {/* 退出登录 */}
        <button className="btn btn-outline"
          onClick={() => { logout(); navigate('/login'); }}
          style={{ width: '100%', height: 46 }}>
          退出登录
        </button>

      </div>

      <TabBar mode="user" />
    </div>
  );
}
